/**
 * Inventory Tab
 *
 * Searchable product table with stock levels, low-stock highlighting,
 * and expandable rows showing full product metadata.
 * Uses useAdminTheme() for light/dark mode aware styling.
 *
 * @module StoreCommandCenter/components/tabs
 */

import React, { useState, useEffect, useCallback, Fragment, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Avatar,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as LowStockIcon,
  CheckCircle as InStockIcon,
  Cancel as OutOfStockIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  NewReleases as NewIcon,
  Star as PopularIcon,
  Block as RestrictedIcon,
  QrCode as BarcodeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShoppingCart as SellableIcon,
  PhoneAndroid as AppIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import Logger from '../../../../utils/Logger';
import config from '../../../../config';
import { getValidToken } from '../../../../services/tokenRefreshService';
import { useAdminTheme, type AdminColorTokens } from '../../../../admin-theme';

// ============================================================================
// Types
// ============================================================================

interface InventoryTabProps {
  storeId: string;
  storeName: string;
}

interface InventoryItem {
  productId: string;
  name: string;
  sku: string;
  price: number;
  image?: string;
  description?: string;
  availableStock: number;
  onHandStock?: number;
  allocatedStock?: number;
  category: string | null;
  categoryId?: string | null;
  brand: string | null;
  barcode: string | null;
  tags: string[];
  lastRefreshedAt: string;
  status?: string;
  legacyExternalId?: string;
  lastModifiedOn?: string;
  syncedAt?: string;
  flags?: {
    isNew?: boolean;
    isPopular?: boolean;
    isRestricted?: boolean;
    isSellable?: boolean;
    isSellableInApp?: boolean;
    isBillOfMaterial?: boolean;
  };
}

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 50; // Larger page size for infinite scroll
const SCROLL_THRESHOLD = 200; // Pixels from bottom to trigger load more

// ============================================================================
// Sub-components
// ============================================================================

interface FlagChipProps {
  label: string;
  active: boolean;
  icon: React.ReactNode;
  colors: AdminColorTokens;
  activeColor?: string;
}

function FlagChip({ label, active, icon, colors, activeColor = colors.successGlow }: FlagChipProps) {
  return (
    <Chip
      icon={<>{icon}</>}
      label={label}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.7rem',
        fontWeight: 500,
        bgcolor: active ? `${activeColor}15` : `${colors.textMuted}10`,
        color: active ? activeColor : colors.textMuted,
        border: `1px solid ${active ? `${activeColor}40` : `${colors.textMuted}20`}`,
        '& .MuiChip-icon': {
          color: active ? activeColor : colors.textMuted,
          fontSize: 14,
        },
      }}
    />
  );
}

interface ExpandedRowProps {
  item: InventoryItem;
  colors: AdminColorTokens;
}

function ExpandedRow({ item, colors }: ExpandedRowProps) {
  return (
    <Box sx={{ p: 3, bgcolor: colors.bgDeepest }}>
      <Grid container spacing={3}>
        {/* Left Column: Description & Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DescriptionIcon sx={{ fontSize: 16, color: colors.info }} />
              <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, letterSpacing: '0.05em' }}>
                DESCRIPTION
              </Typography>
            </Box>
            <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: item.description ? colors.textSecondary : colors.textMuted }}>
                {item.description || 'No description available'}
              </Typography>
            </Paper>
          </Box>

          {/* Product Flags */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <InfoIcon sx={{ fontSize: 16, color: colors.warning }} />
              <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, letterSpacing: '0.05em' }}>
                PRODUCT FLAGS
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <FlagChip
                label="Sellable"
                active={item.flags?.isSellable ?? false}
                icon={<SellableIcon sx={{ fontSize: 14 }} />}
                colors={colors}
                activeColor={colors.successGlow}
              />
              <FlagChip
                label="Selling in App"
                active={item.flags?.isSellableInApp ?? false}
                icon={<AppIcon sx={{ fontSize: 14 }} />}
                colors={colors}
                activeColor={colors.info}
              />
              <FlagChip
                label="Popular"
                active={item.flags?.isPopular ?? false}
                icon={<PopularIcon sx={{ fontSize: 14 }} />}
                colors={colors}
                activeColor={colors.warning}
              />
              <FlagChip
                label="New"
                active={item.flags?.isNew ?? false}
                icon={<NewIcon sx={{ fontSize: 14 }} />}
                colors={colors}
                activeColor={colors.successGlow}
              />
              <FlagChip
                label="Restricted (18+)"
                active={item.flags?.isRestricted ?? false}
                icon={<RestrictedIcon sx={{ fontSize: 14 }} />}
                colors={colors}
                activeColor={colors.errorGlow}
              />
            </Box>
          </Box>

          {/* Stock Details */}
          <Box>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, letterSpacing: '0.05em', mb: 1.5, display: 'block' }}>
              STOCK BREAKDOWN
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 1, flex: 1, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: colors.successGlow }}>
                  {item.availableStock}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>Available</Typography>
              </Paper>
              <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 1, flex: 1, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: colors.info }}>
                  {item.onHandStock ?? 0}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>On Hand</Typography>
              </Paper>
              <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 1, flex: 1, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: colors.warning }}>
                  {item.allocatedStock ?? 0}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>Allocated</Typography>
              </Paper>
            </Box>
          </Box>
        </Grid>

        {/* Right Column: Additional Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Product Image Placeholder */}
          <Paper
            sx={{
              p: 2,
              bgcolor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 2,
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Avatar
              src={item.image || undefined}
              variant="rounded"
              sx={{
                width: 120,
                height: 120,
                bgcolor: colors.bgElevated,
                border: `2px solid ${colors.border}`,
                mb: 1,
                fontSize: '2rem',
              }}
            >
              {item.name?.charAt(0) || '?'}
            </Avatar>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {item.image ? 'Product Image' : 'No image available'}
            </Typography>
          </Paper>

          {/* Sync Info */}
          <Paper sx={{ p: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600, letterSpacing: '0.05em', mb: 1.5, display: 'block' }}>
              SYNC INFO
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {item.legacyExternalId && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>Legacy ID</Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontFamily: 'monospace' }}>
                    {item.legacyExternalId}
                  </Typography>
                </Box>
              )}
              {item.barcode && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>Barcode</Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontFamily: 'monospace' }}>
                    {item.barcode}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>Status</Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {item.status || 'Unknown'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>Last Sync</Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {item.syncedAt ? new Date(item.syncedAt).toLocaleString() : '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>Stock Refresh</Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {item.lastRefreshedAt ? new Date(item.lastRefreshedAt).toLocaleString() : '—'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Inventory Tab Content
 */
export function InventoryTab({ storeId, storeName }: InventoryTabProps) {
  const { mode, colors } = useAdminTheme();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  /**
   * Fetch inventory data (for initial load or filter changes)
   */
  const fetchInventory = useCallback(async (resetList = true) => {
    if (!storeId) return;

    // Prevent duplicate loads
    if (!resetList && loadingMoreRef.current) return;

    if (resetList) {
      setLoading(true);
      pageRef.current = 1;
    } else {
      setLoadingMore(true);
      loadingMoreRef.current = true;
    }
    setError(null);

    const currentPage = pageRef.current;

    try {
      const token = await getValidToken();
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(PAGE_SIZE),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(showLowStockOnly && { lowStockOnly: 'true' }),
      });

      const response = await fetch(
        `${config.API_URL}/admin/stores/${storeId}/inventory?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const newItems = data.inventory || [];
      const totalCount = data.pagination?.total || 0;

      if (resetList) {
        setInventory(newItems);
      } else {
        setInventory(prev => [...prev, ...newItems]);
      }

      setTotal(totalCount);
      const moreAvailable = currentPage * PAGE_SIZE < totalCount;
      setHasMore(moreAvailable);

      if (!resetList) {
        pageRef.current = currentPage + 1;
      }
    } catch (err: any) {
      Logger.error('Failed to fetch inventory:', err);
      setError('Failed to load inventory');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [storeId, debouncedSearch, showLowStockOnly]);

  // Initial fetch and when filters change
  useEffect(() => {
    fetchInventory(true);
  }, [fetchInventory]);

  // Handle scroll for infinite loading
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (loadingMoreRef.current || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD) {
        fetchInventory(false);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, fetchInventory]);

  /**
   * Get stock status styling
   */
  const getStockStatus = (stock: number) => {
    if (stock === 0) {
      return { label: 'Out', color: colors.errorGlow, icon: <OutOfStockIcon /> };
    }
    if (stock <= 5) {
      return { label: 'Low', color: colors.warning, icon: <LowStockIcon /> };
    }
    return { label: 'OK', color: colors.successGlow, icon: <InStockIcon /> };
  };

  /**
   * Toggle row expansion
   */
  const handleRowClick = (productId: string) => {
    setExpandedRow(expandedRow === productId ? null : productId);
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search products..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 250,
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.bgElevated,
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.textMuted },
              '&.Mui-focused fieldset': { borderColor: colors.info },
            },
            '& .MuiInputBase-input': { color: colors.textPrimary },
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={showLowStockOnly}
              onChange={(e) => setShowLowStockOnly(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: colors.warning },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.warning },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Low stock only
            </Typography>
          }
        />

        <Tooltip title="Refresh inventory">
          <span>
            <IconButton
              onClick={() => fetchInventory(true)}
              disabled={loading}
              sx={{
                color: colors.textSecondary,
                bgcolor: colors.bgElevated,
                border: `1px solid ${colors.border}`,
                '&:hover': { bgcolor: colors.border, color: colors.textPrimary },
                '&.Mui-disabled': { color: colors.textMuted, bgcolor: colors.bgElevated },
              }}
            >
              <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </span>
        </Tooltip>

        {/* Item count */}
        <Typography variant="body2" sx={{ color: colors.textMuted, ml: 'auto' }}>
          {inventory.length} of {total} products
        </Typography>
      </Box>

      {/* Hint for expandable rows */}
      <Alert
        severity="info"
        sx={{
          mb: 2,
          bgcolor: `${colors.info}08`,
          border: `1px solid ${colors.info}20`,
          '& .MuiAlert-icon': { color: colors.info },
          color: colors.textSecondary,
          py: 0.5,
        }}
      >
        Click on a row to expand and view full product details, flags, and metadata.
      </Alert>

      {/* Error state */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            bgcolor: `${colors.errorGlow}10`,
            border: `1px solid ${colors.errorGlow}40`,
            '& .MuiAlert-icon': { color: colors.errorGlow },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Table with infinite scroll */}
      <TableContainer
        ref={scrollContainerRef}
        component={Paper}
        sx={{
          bgcolor: colors.bgElevated,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          maxHeight: 'calc(100vh - 380px)',
          minHeight: 400,
          overflow: 'auto',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600, width: 40 }}>
                {/* Expand */}
              </TableCell>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600 }}>
                Product
              </TableCell>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600 }}>
                SKU
              </TableCell>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600 }}>
                Category
              </TableCell>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600 }} align="right">
                Price
              </TableCell>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600 }} align="center">
                Stock
              </TableCell>
              <TableCell sx={{ color: colors.textMuted, borderColor: colors.border, fontWeight: 600 }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && inventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ borderColor: colors.border }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: colors.info }} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : inventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ borderColor: colors.border }}>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.textMuted, textAlign: 'center', py: 4 }}
                  >
                    {search ? 'No products matching your search' : 'No products in inventory'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              inventory.map((item) => {
                const stockStatus = getStockStatus(item.availableStock);
                const isExpanded = expandedRow === item.productId;

                return (
                  <Fragment key={item.productId}>
                    <TableRow
                      onClick={() => handleRowClick(item.productId)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: `${colors.bgCard}` },
                        opacity: item.availableStock === 0 ? 0.6 : 1,
                        bgcolor: isExpanded ? colors.bgCard : 'transparent',
                      }}
                    >
                      {/* Expand Icon */}
                      <TableCell sx={{ borderColor: colors.border, width: 40 }}>
                        <IconButton size="small" sx={{ color: colors.textMuted }}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>

                      {/* Product */}
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={item.image}
                            variant="rounded"
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: colors.bgCard,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            {item.name?.charAt(0) || '?'}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: colors.textPrimary,
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 180,
                                }}
                              >
                                {item.name}
                              </Typography>
                              {/* Product Flags (compact icons) */}
                              {item.flags?.isNew && (
                                <Tooltip title="New Product">
                                  <NewIcon sx={{ fontSize: 14, color: colors.successGlow }} />
                                </Tooltip>
                              )}
                              {item.flags?.isPopular && (
                                <Tooltip title="Popular">
                                  <PopularIcon sx={{ fontSize: 14, color: colors.warning }} />
                                </Tooltip>
                              )}
                              {item.flags?.isRestricted && (
                                <Tooltip title="Restricted (Age 18+)">
                                  <RestrictedIcon sx={{ fontSize: 14, color: colors.errorGlow }} />
                                </Tooltip>
                              )}
                              {item.flags?.isSellableInApp === false && (
                                <Tooltip title="Not selling in app">
                                  <AppIcon sx={{ fontSize: 14, color: colors.textMuted, opacity: 0.5 }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.brand && (
                                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                  {item.brand}
                                </Typography>
                              )}
                              {item.barcode && (
                                <Tooltip title={`Barcode: ${item.barcode}`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <BarcodeIcon sx={{ fontSize: 12, color: colors.textMuted }} />
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* SKU */}
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace' }}>
                          {item.sku}
                        </Typography>
                      </TableCell>

                      {/* Category */}
                      <TableCell sx={{ borderColor: colors.border }}>
                        {item.category ? (
                          <Chip
                            icon={<CategoryIcon sx={{ fontSize: 14 }} />}
                            label={item.category}
                            size="small"
                            sx={{
                              bgcolor: `${colors.info}15`,
                              color: colors.info,
                              border: `1px solid ${colors.info}30`,
                              fontSize: '0.7rem',
                            }}
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Price */}
                      <TableCell sx={{ borderColor: colors.border }} align="right">
                        <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                          R{item.price.toFixed(2)}
                        </Typography>
                      </TableCell>

                      {/* Stock */}
                      <TableCell sx={{ borderColor: colors.border }} align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            color: stockStatus.color,
                            fontWeight: 600,
                            fontFamily: 'monospace',
                          }}
                        >
                          {item.availableStock}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ borderColor: colors.border }}>
                        <Chip
                          icon={React.cloneElement(stockStatus.icon, { sx: { fontSize: 14 } } as any)}
                          label={stockStatus.label}
                          size="small"
                          sx={{
                            bgcolor: `${stockStatus.color}15`,
                            color: stockStatus.color,
                            border: `1px solid ${stockStatus.color}30`,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, borderColor: colors.border }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <ExpandedRow item={item} colors={colors} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Loading more indicator */}
      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color: colors.info }} />
          <Typography variant="body2" sx={{ color: colors.textMuted, ml: 2 }}>
            Loading more products...
          </Typography>
        </Box>
      )}

      {/* End of list indicator */}
      {!loading && !loadingMore && !hasMore && inventory.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            Showing all {total} products
          </Typography>
        </Box>
      )}

      {/* Keyframe for spinner */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
}

export default InventoryTab;
